import { useChatboxStore } from "../../../store";
import { FormFieldInput } from "./FormFieldInput";
import type { FormState } from "./type";
import type {
  FormFieldData,
  FormMessageData,
  FormValue,
  FormValueType,
} from "@/model/form";
import {
  MessagingParticipantType,
  type MessagingMessage,
} from "@/model/messaging";
import { cn } from "@deputy-ai/common";
import { LoadingIcon } from "@deputy-ai/icons";
import { Button } from "@deputy-ai/ui/button/Button";
import { Form, useForm } from "@deputy-ai/ui/form/Form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useMemo, useState } from "react";
import * as z from "zod";

export const MessageForm = ({ message }: { message: MessagingMessage }) => {
  const { formFields, formMessage, disabledInput } = useParseFormFieldData({
    message,
  });

  const { formSchema, defaultValues } = useFormSchema({ formFields });
  const form = useForm<Record<string, FormValueType>>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const { formState, onSubmit } = useHandleSubmit({ message, formFields });

  return (
    <div className="xsm:w-96 flex w-72 flex-col rounded-xl bg-white/50">
      <Form
        {...form}
        className="flex h-full flex-col gap-2 overflow-y-auto p-4 font-medium"
        onSubmit={form.handleSubmit(onSubmit)}
      >
        {formMessage && (
          <div className="mb-2 border-b border-gray-200 pb-2">
            {formMessage}
          </div>
        )}
        {formFields.map((formField, index) => (
          <Form.Field
            key={index}
            control={form.control}
            name={formField.def.label}
            labelProps={{
              className: cn(
                !formField.def.attrs?.optional && "required",
                "flex items-center gap-2"
              ),
            }}
            label={formField.def.label}
            className="flex flex-col items-start justify-center gap-1"
          >
            {({ field }) => (
              <Form.Control>
                <FormFieldInput
                  form={form}
                  field={field}
                  formField={formField}
                  formState={formState}
                  disabled={disabledInput}
                />
              </Form.Control>
            )}
          </Form.Field>
        ))}
        {formState !== "submitted" && !disabledInput && (
          <div className="flex flex-row justify-end">
            <Button
              size="sm"
              className="bg-black"
              type="submit"
              disabled={formState === "submitting"}
            >
              {formState === "submitting" && (
                <LoadingIcon className="-ml-1 h-6 w-6" />
              )}
              Confirm
            </Button>
          </div>
        )}
      </Form>
    </div>
  );
};

const useParseFormFieldData = ({ message }: { message: MessagingMessage }) => {
  const formData = message.payload.data as unknown as FormMessageData;
  const formDef = formData?.form_def;
  const formValues = formData?.form_values?.values;

  const {
    use: { options },
  } = useChatboxStore();

  const formFields = useMemo(() => {
    if (!formDef?.fields) {
      return [];
    }
    return formDef.fields.map(
      (def) =>
        ({
          def,
          value: formValues?.[def.label],
        }) satisfies FormFieldData
    );
  }, [formDef, formValues]);
  return {
    formMessage: formDef?.message,
    formFields,
    isFormSubmitted: formData?.submitted,
    disabledInput:
      options?.participants?.myself?.type ===
      MessagingParticipantType.HumanAgent,
  };
};

const useFormSchema = ({ formFields }: { formFields: FormFieldData[] }) => {
  const formSchema = useMemo(() => {
    const obj: Record<string, z.ZodType> = {};
    formFields.forEach((field) => {
      let zobj: z.ZodType;
      switch (field.def.field_type) {
        case "string": {
          let zs = z.string();
          if (!field.def.attrs?.optional) {
            zs = zs.min(1);
          }
          if (field.def.attrs?.email) {
            zs = zs.email();
          }
          zobj = zs;
          break;
        }
        case "number":
          zobj = z.number();
          break;
        case "file":
          zobj = z.string();
      }
      if (field.def.attrs?.optional) {
        zobj = zobj.optional();
      }
      obj[field.def.label] = zobj;
    });
    return z.object(obj);
  }, [formFields]);

  const defaultValues: Record<string, FormValueType> = useMemo(() => {
    const obj: Record<string, FormValueType> = {};
    formFields.forEach((field) => {
      if (field.value) {
        if (field.def.field_type === "file") {
          obj[field.def.label] = JSON.stringify(field.value);
        } else {
          obj[field.def.label] = field.value.value;
        }
        return;
      }
      switch (field.def.field_type) {
        case "string":
          obj[field.def.label] = "";
          break;
        case "number":
          obj[field.def.label] = 0;
          break;
        case "file":
          break;
      }
    });
    return obj;
  }, [formFields]);

  return {
    formSchema,
    defaultValues,
  };
};

const useHandleSubmit = ({
  formFields,
  message,
}: {
  formFields: FormFieldData[];
  message: MessagingMessage;
}) => {
  const {
    use: { lastMessage },
    bind: { submitForm },
  } = useChatboxStore();

  const formData = message.payload.data as unknown as FormMessageData;

  const [formState, setFormState] = useState<FormState>("init");
  const actualFormState: FormState =
    !!formData?.submitted || lastMessage?.uid !== message.uid
      ? "submitted"
      : formState;

  const onSubmit = useCallback(
    async (values: Record<string, FormValueType>) => {
      setFormState("submitting");
      try {
        const formValues: Record<string, FormValue> = {};

        formFields.forEach((f) => {
          const value = values[f.def.label];
          if (!value) {
            return;
          }
          if (f.def.field_type === "file" && typeof value === "string") {
            formValues[f.def.label] = JSON.parse(value);
          } else {
            formValues[f.def.label] = {
              label: f.def.label,
              value,
            };
          }
          formValues[f.def.label].metadata = f.def.metadata;
        });
        await submitForm({ messageUid: message.uid, formValues });
      } catch (e) {
        console.error("fail to submit form", e);
        setFormState("error");
      } finally {
        setFormState("submitted");
      }
    },
    [formFields, message]
  );
  return {
    onSubmit,
    formState: actualFormState,
  };
};
